<template>
  <Modal classes="auto-report-modal" @close="close">
    <header class="auto-report-modal__header">
      <h2>Отчитане с глас, снимка или свободен текст</h2>

      <button
      class="sienit auto-report-modal__close"
        @click="close"
      >
        F
    </button>
    </header>

    <main
      class="auto-report-modal__content"
      :class="{
        'auto-report-modal__content--audio-active': isAudioActivityVisible,
      }"
    >
      <section
        v-if="isAudioActivityVisible"
        class="auto-report-modal__audio-activity"
        aria-live="polite"
      >
        <button
          v-if="isRecording"
          class="auto-report-modal__audio-visual auto-report-modal__audio-stop"
          type="button"
          aria-label="Спри записа и го изпрати"
          title="Спри записа и го изпрати"
          @click="toggleRecording"
        >
          <img :src="workingMicrophoneAnimation" alt="" />
        </button>
        <p
          class="auto-report-modal__audio-status"
          :class="{
            'auto-report-modal__audio-status--recording': isRecording,
          }"
        >
          {{ audioActivityMessage }}
        </p>
      </section>

      <template v-else>
      <p class="auto-report-modal__intro">
        Изберете директно диктуване, или напишете със свободен текст по какво
        сте работили днес. Също може да заснемете или прикачите снимка на
        ръкописен текст.
      </p>

      <form
        class="auto-report-modal__input-row"
        @submit.prevent="submitText"
      >
        <BaseField
          field-class="auto-report-modal__field"
          :hide-icon="true"
          placeholder="Напишете отчет и натиснете Enter"
          :value="freeText"
          @input="setFreeText"
        />
        <button
          class="auto-report-modal__submit"
          type="submit"
          :disabled="isLoading || isRecording || isStartingRecording"
        >
          Изпрати текста
        </button>
        <button
          class="auto-report-modal__round-button"
          :class="{
            'auto-report-modal__round-button--recording': isRecording,
          }"
          type="button"
          :aria-label="
            isRecording ? 'Спри записа и го изпрати' : 'Запиши отчет с глас'
          "
          :title="
            isRecording ? 'Спри записа и го изпрати' : 'Запиши отчет с глас'
          "
          :disabled="isLoading || isStartingRecording"
          @click="toggleRecording"
        >
          <img
            src="@/assets/img/report/auto-report-microphone.svg"
            alt=""
          />
        </button>
        <button
          class="auto-report-modal__round-button"
          :class="{
            'auto-report-modal__round-button--selected': selectedImageUrl,
          }"
          type="button"
          aria-label="Прикачи снимка"
          title="Прикачи снимка"
          :disabled="isLoading || isRecording || isStartingRecording"
          @click="openImagePicker"
        >
          <img
            src="@/assets/img/report/auto-report-camera.svg"
            alt=""
          />
        </button>
        <input
          ref="imageInput"
          class="auto-report-modal__file-input"
          type="file"
          accept="image/jpeg,image/png,image/webp"
          @change="selectImage"
        />
      </form>

      <p v-if="selectedImageName" class="auto-report-modal__selected-file">
        Прикачена снимка: <strong>{{ selectedImageName }}</strong>
      </p>

      <div
        v-if="isLoading || errorMessage"
        class="auto-report-modal__response"
        aria-live="polite"
      >
        <p v-if="isLoading" class="auto-report-modal__status">
          {{ loadingMessage }}
        </p>
        <p
          v-if="errorMessage"
          class="auto-report-modal__status auto-report-modal__status--error"
        >
          {{ errorMessage }}
        </p>
      </div>

      <section v-if="result" class="auto-report-modal__review">
        <p class="auto-report-modal__review-hint">
          Коригирайте разпознатия текст в полето по-горе и натиснете Enter,
          ако искате да го обработим отново.
        </p>

        <div
          v-if="draftRows.length"
          class="auto-report-modal__review-table-wrap"
        >
          <table class="auto-report-modal__review-table">
            <thead>
              <tr>
                <th>Артикул</th>
                <th>Количество</th>
                <th>Ед. м.</th>
                <th>Цена</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in draftRows" :key="index">
                <td
                  class="auto-report-modal__review-item"
                  data-label="Артикул"
                >
                  <BaseField
                    field-class="auto-report-modal__review-field"
                    :hide-icon="true"
                    :value="row.name"
                    :options="itemOptions(row.name)"
                    option-label="name"
                    :filter-options="false"
                    @input="setDraftValue(index, 'name', $event)"
                    @select="selectItemSuggestion(index, $event)"
                  />
                </td>
                <td data-label="Количество">
                  <BaseField
                    field-class="auto-report-modal__review-field"
                    :hide-icon="true"
                    type="number"
                    :min="0"
                    :value="row.incomingQuantity"
                    @input="setDraftValue(index, 'incomingQuantity', $event)"
                  />
                </td>
                <td data-label="Ед. м.">
                  <BaseField
                    field-class="auto-report-modal__review-field"
                    :hide-icon="true"
                    :value="row.unit"
                    :options="unitSuggestions"
                    @input="setDraftValue(index, 'unit', $event)"
                  />
                </td>
                <td data-label="Цена">
                  <BaseField
                    field-class="auto-report-modal__review-field"
                    :hide-icon="true"
                    type="number"
                    :min="0"
                    :value="row.price"
                    @input="setDraftValue(index, 'price', $event)"
                  />
                </td>
                <td>
                  <button
                    class="auto-report-modal__remove-row"
                    type="button"
                    aria-label="Премахни реда"
                    title="Премахни реда"
                    @click="removeDraftRow(index)"
                  >
                    ×
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <p v-else class="auto-report-modal__review-empty">
          Не са намерени редове за добавяне. Коригирайте текста и го изпратете
          отново.
        </p>

        <ul v-if="reviewWarnings.length" class="auto-report-modal__warnings">
          <li v-for="warning in reviewWarnings" :key="warning">
            {{ warning }}
          </li>
        </ul>

        <div class="auto-report-modal__review-actions">
          <button
            class="btn auto-report-modal__add-row"
            type="button"
            @click="addDraftRow"
          >
            Добави ред
          </button>
          <button
            class="btn auto-report-modal__apply"
            type="button"
            :disabled="!draftRows.length"
            @click="applyDraft"
          >
            Приложи в отчета
          </button>
        </div>
      </section>

      <section v-else class="auto-report-modal__example">
        <div class="auto-report-modal__example-text">
          <strong>Примерен отчет:</strong>
          <p>
            Днес използвах следното:<br />
            - Заготовка от арматурно желязо оребрено СП Л N10.00 В500 244 kg
            по 0.85 €<br />
            - Инжекционна смола HIT-RE 500 V4/500 10 БР по 55.82 €<br />
            - Фракция 0-40 мм 1328 тона по 5,6 €<br />
            - Фракция 0-63 мм 5669,58 тона по 5,6 €<br />
            - Скална маса 25,74 тона по 2,6 €
          </p>
        </div>
        <div class="auto-report-modal__examples">
          <button
            class="auto-report-modal__example-action"
            type="button"
            @click="toggleExampleAudio"
          >
            <span class="auto-report-modal__example-tile">
              <img
                v-if="!isExamplePlaying"
                class="auto-report-modal__play-icon"
                src="@/assets/img/report/auto-report-play.svg"
                alt=""
              />
              <img
                v-else
                class="auto-report-modal__play-icon"
                src="@/assets/img/report/auto-report-pause.svg"
                alt=""
              />
            </span>
            <span>{{
              isExamplePlaying ? "Спри аудиото" : "Чуй примерното аудио"
            }}</span>
          </button>
          <audio
            ref="exampleAudio"
            :src="exampleAudio"
            preload="metadata"
            @play="isExamplePlaying = true"
            @pause="isExamplePlaying = false"
            @ended="isExamplePlaying = false"
            @error="handleExampleAudioError"
          />
          <button
            class="auto-report-modal__example-action auto-report-modal__example-action--image"
            type="button"
            @click="isExampleImageOpened = true"
          >
            <span
              class="auto-report-modal__example-tile"
            >
              <img :src="exampleImage" alt="Примерна ръкописна бележка" />
            </span>
            <span>Виж примерна снимка</span>
          </button>
        </div>
      </section>

      <p v-if="!result" class="auto-report-modal__hint">
        Дори нещо да е объркано, може да се редактира с коректното име и
        количество.
      </p>

      <div
        v-if="isExampleImageOpened"
        class="auto-report-modal__image-preview"
        @click.self="isExampleImageOpened = false"
      >
        <button
          type="button"
          aria-label="Затвори примерната снимка"
          @click="isExampleImageOpened = false"
        >
          ×
        </button>
        <img :src="exampleImage" alt="Примерна ръкописна бележка" />
      </div>
      </template>
    </main>
  </Modal>
</template>

<script>
import Modal from "../components/Modal.vue";
import BaseField from "./BaseField.vue";

export default {
  name: "AiReportModal",
  components: {
    BaseField,
    Modal,
  },
  props: {
    itemSuggestions: {
      type: Array,
      default: () => [],
    },
    unitSuggestions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      exampleAudio: `${process.env.BASE_URL}audio/ai-report-example.m4a`,
      exampleImage: require("../../assets/img/report/auto-report-example.png"),
      workingMicrophoneAnimation: require("../../assets/img/report/mic-working_animated.svg"),
      freeText: "",
      isExampleImageOpened: false,
      isExamplePlaying: false,
      isLoading: false,
      isRecording: false,
      isPreparingAudio: false,
      isStartingRecording: false,
      isDestroyed: false,
      recordingRequestId: 0,
      errorMessage: "",
      result: null,
      draftRows: [],
      activeMode: "",
      audioChunks: [],
      audioStream: null,
      mediaRecorder: null,
      submitRecordingOnStop: false,
      selectedImageName: "",
      selectedImageUrl: "",
    };
  },
  computed: {
    reviewWarnings() {
      if (!this.result) return [];

      return [
        ...(Array.isArray(this.result.warnings) ? this.result.warnings : []),
        ...(Array.isArray(this.result.unparsedText)
          ? this.result.unparsedText.map((text) => `Неразпознато: ${text}`)
          : []),
      ].filter(Boolean);
    },
    loadingMessage() {
      const messages = {
        text: "Обработка…",
        image: "Разчитаме снимката…",
        audio: "Разпознаваме и обработваме записа…",
      };

      return messages[this.activeMode] || "Обработваме отчета…";
    },
    isAudioActivityVisible() {
      return Boolean(
        this.isRecording ||
          this.isPreparingAudio ||
          (this.isLoading && this.activeMode === "audio")
      );
    },
    audioActivityMessage() {
      return this.isRecording
        ? "Записваме… Натиснете микрофона отново, за да спрете и изпратите."
        : "Разпознаваме и обработваме записа…";
    },
  },
  beforeDestroy() {
    this.isDestroyed = true;
    this.recordingRequestId += 1;
    this.isStartingRecording = false;
    this.stopRecording(false);
    this.stopExampleAudio();
    this.releaseSelectedImage();
  },
  methods: {
    close() {
      this.$emit("close");
    },
    setFreeText(value) {
      this.freeText = value;
      this.$emit("text-change", value);
    },
    prepareResult(result) {
      const diary =
        result && result.diary && typeof result.diary === "object"
          ? result.diary
          : {};
      const rows = Array.isArray(diary.buildingMaterials)
        ? diary.buildingMaterials
        : [];

      this.result = result;
      this.freeText = String((result && result.sourceText) || this.freeText);
      this.draftRows = rows.map((row) => ({
        name: row.name || "",
        documentNumber: row.documentNumber || "",
        unit: row.unit || "",
        incomingQuantity:
          row.incomingQuantity === null || row.incomingQuantity === undefined
            ? ""
            : row.incomingQuantity,
        price:
          row.price === null || row.price === undefined ? "" : row.price,
        source: row.source || "",
        place: row.place || "",
        note: row.note || "",
      }));
    },
    normalizeSuggestionText(value) {
      return String(value || "")
        .toLocaleLowerCase("bg-BG")
        .replace(/[„“"'’]/g, "")
        .replace(/[^a-zа-я0-9]+/gi, " ")
        .trim();
    },
    editDistance(leftValue, rightValue) {
      const left = String(leftValue || "");
      const right = String(rightValue || "");
      const previous = Array.from(
        { length: right.length + 1 },
        (value, index) => index
      );

      for (let leftIndex = 1; leftIndex <= left.length; leftIndex += 1) {
        const current = [leftIndex];

        for (
          let rightIndex = 1;
          rightIndex <= right.length;
          rightIndex += 1
        ) {
          const substitutionCost =
            left[leftIndex - 1] === right[rightIndex - 1] ? 0 : 1;
          current[rightIndex] = Math.min(
            current[rightIndex - 1] + 1,
            previous[rightIndex] + 1,
            previous[rightIndex - 1] + substitutionCost
          );
        }

        previous.splice(0, previous.length, ...current);
      }

      return previous[right.length];
    },
    itemSuggestionScore(value, suggestion) {
      const target = this.normalizeSuggestionText(value);
      const candidate = this.normalizeSuggestionText(suggestion.name);
      const catalogBonus =
        suggestion.suggestionOrigin === "catalog" ? 0.08 : 0;

      if (!target || !candidate) return catalogBonus;
      if (target === candidate) return 3 + catalogBonus;
      if (candidate.includes(target) || target.includes(candidate)) {
        return 2 + catalogBonus;
      }

      const targetTokens = new Set(target.split(" "));
      const candidateTokens = candidate.split(" ");
      const commonTokens = candidateTokens.filter((token) =>
        targetTokens.has(token)
      ).length;
      const tokenScore =
        commonTokens / Math.max(targetTokens.size, candidateTokens.length, 1);
      const comparedLength = Math.max(target.length, candidate.length, 1);
      const similarity =
        1 - this.editDistance(target, candidate) / comparedLength;

      return Math.max(tokenScore, similarity) + catalogBonus;
    },
    itemOptions(value) {
      return this.itemSuggestions
        .map((suggestion, index) => ({
          suggestion,
          index,
          score: this.itemSuggestionScore(value, suggestion),
        }))
        .sort((left, right) => {
          if (right.score !== left.score) return right.score - left.score;
          return left.index - right.index;
        })
        .slice(0, 8)
        .map((item) => item.suggestion);
    },
    setDraftValue(index, key, value) {
      this.$set(this.draftRows[index], key, value);
    },
    selectItemSuggestion(index, suggestion) {
      const row = this.draftRows[index];
      if (!row || !suggestion) return;

      this.$set(row, "name", suggestion.name || row.name);
      if (!row.unit && suggestion.unit) {
        this.$set(row, "unit", suggestion.unit);
      }
      if (
        (row.price === "" || row.price === null || row.price === undefined) &&
        suggestion.price !== "" &&
        suggestion.price !== null &&
        suggestion.price !== undefined
      ) {
        this.$set(row, "price", suggestion.price);
      }
    },
    addDraftRow() {
      this.draftRows.push({
        name: "",
        documentNumber: "",
        unit: "",
        incomingQuantity: "",
        price: "",
        source: "",
        place: "",
        note: "",
      });
    },
    removeDraftRow(index) {
      this.draftRows.splice(index, 1);
    },
    applyDraft() {
      const rows = this.draftRows.filter((row) => String(row.name || "").trim());
      if (!rows.length) {
        this.showError("Добавете поне един разпознат артикул.");
        return;
      }

      this.$emit("result", {
        ...this.result,
        sourceText: this.freeText.trim(),
        location: {
          project: "",
          object: "",
          subobject: "",
          part: "",
        },
        diary: {
          buildingMaterials: rows,
          movedInventory: [],
          consumedWithoutFinalResult: [],
          receivedInventory: [],
          personnel: [],
          mechanizations: [],
        },
      });
    },
    async submitText() {
      const text = this.freeText.trim();
      if (
        !text ||
        this.isLoading ||
        this.isRecording ||
        this.isStartingRecording
      ) {
        if (!text) this.showError("Въведете текст за отчета.");
        return;
      }

      await this.submitRequest("text", { mode: "text", text });
    },
    openImagePicker() {
      if (this.isLoading || this.isRecording || this.isStartingRecording) return;
      this.$refs.imageInput.value = "";
      this.$refs.imageInput.click();
    },
    async selectImage(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;

      this.releaseSelectedImage();
      this.selectedImageName = file.name;
      this.selectedImageUrl = URL.createObjectURL(file);
      this.$emit("image-selected", file);

      await this.submitFile("image", file);
    },
    releaseSelectedImage() {
      if (this.selectedImageUrl) URL.revokeObjectURL(this.selectedImageUrl);
      this.selectedImageUrl = "";
    },
    async toggleRecording() {
      if (this.isLoading || this.isStartingRecording) return;

      if (this.isRecording) {
        this.stopRecording(true);
        return;
      }

      if (
        !navigator.mediaDevices ||
        !navigator.mediaDevices.getUserMedia ||
        typeof MediaRecorder === "undefined"
      ) {
        this.showError("Този браузър не поддържа запис от микрофон.");
        return;
      }

      this.stopExampleAudio();
      this.clearResponse();
      const requestId = ++this.recordingRequestId;
      this.isStartingRecording = true;

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });

        if (this.isDestroyed || requestId !== this.recordingRequestId) {
          stream.getTracks().forEach(track => track.stop());
          return;
        }

        this.audioStream = stream;
        const mimeType = this.preferredAudioMimeType();
        const options = mimeType ? { mimeType } : undefined;
        const recorder = new MediaRecorder(stream, options);

        this.audioChunks = [];
        this.mediaRecorder = recorder;
        this.submitRecordingOnStop = true;

        recorder.ondataavailable = event => {
          if (event.data && event.data.size) this.audioChunks.push(event.data);
        };
        recorder.onerror = () => {
          this.submitRecordingOnStop = false;
          this.isRecording = false;
          this.isPreparingAudio = false;
          this.releaseAudioStream();
          if (!this.isDestroyed) {
            this.showError("Възникна проблем при записа от микрофона.");
          }
        };
        recorder.onstop = this.handleRecordingStopped;
        recorder.start();
        this.isRecording = true;
      } catch (error) {
        this.releaseAudioStream();
        if (!this.isDestroyed && requestId === this.recordingRequestId) {
          this.showError(
            error && error.name === "NotAllowedError"
              ? "Разрешете достъпа до микрофона и опитайте отново."
              : "Микрофонът не можа да бъде стартиран."
          );
        }
      } finally {
        if (requestId === this.recordingRequestId) {
          this.isStartingRecording = false;
        }
      }
    },
    stopRecording(shouldSubmit = true) {
      if (!this.mediaRecorder) {
        this.isPreparingAudio = false;
        this.releaseAudioStream();
        return;
      }

      this.submitRecordingOnStop = shouldSubmit;
      this.isPreparingAudio = shouldSubmit;
      this.isRecording = false;

      if (this.mediaRecorder.state !== "inactive") {
        this.mediaRecorder.stop();
      } else {
        this.releaseAudioStream();
      }
    },
    async handleRecordingStopped() {
      const chunks = this.audioChunks;
      const recorder = this.mediaRecorder;
      const shouldSubmit = this.submitRecordingOnStop;
      const mimeType =
        (recorder && recorder.mimeType) ||
        (chunks[0] && chunks[0].type) ||
        "audio/webm";

      this.audioChunks = [];
      this.mediaRecorder = null;
      this.submitRecordingOnStop = false;
      this.releaseAudioStream();

      if (!shouldSubmit || !chunks.length) {
        this.isPreparingAudio = false;
        return;
      }

      const extension = mimeType.includes("mp4") ? "m4a" : "webm";
      const blob = new Blob(chunks, { type: mimeType });
      const file = new File(
        [blob],
        `ai-report-${Date.now()}.${extension}`,
        { type: mimeType }
      );

      try {
        await this.submitFile("audio", file);
      } finally {
        this.isPreparingAudio = false;
      }
    },
    releaseAudioStream() {
      if (this.audioStream) {
        this.audioStream.getTracks().forEach(track => track.stop());
      }
      this.audioStream = null;
    },
    preferredAudioMimeType() {
      const mimeTypes = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4",
      ];

      return mimeTypes.find(type => MediaRecorder.isTypeSupported(type)) || "";
    },
    async submitFile(mode, file) {
      const formData = new FormData();
      formData.append("mode", mode);
      formData.append("file", file);

      await this.submitRequest(mode, formData);
    },
    async submitRequest(mode, payload) {
      this.clearResponse();
      this.activeMode = mode;
      this.isLoading = true;

      try {
        const response = await this.$http.post(
          `${process.env.VUE_APP_BASE_URI}/ai-report/draft`,
          payload
        );
        this.prepareResult(response.data);
      } catch (error) {
        const message =
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          "Отчетът не можа да бъде обработен.";
        this.showError(message);
      } finally {
        this.isLoading = false;
        this.activeMode = "";
      }
    },
    clearResponse() {
      this.errorMessage = "";
      this.result = null;
      this.draftRows = [];
    },
    showError(message) {
      this.errorMessage = message;
      this.result = null;
      this.draftRows = [];
      this.$snotify.error(message);
    },
    toggleExampleAudio() {
      const audio = this.$refs.exampleAudio;
      if (!audio) return;

      if (!audio.paused) {
        audio.pause();
        return;
      }

      audio.play().catch(() => {
        this.handleExampleAudioError();
      });
    },
    stopExampleAudio() {
      const audio = this.$refs.exampleAudio;
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
      this.isExamplePlaying = false;
    },
    handleExampleAudioError() {
      this.isExamplePlaying = false;
      this.$snotify.error("Примерното аудио не можа да бъде възпроизведено.");
    },
  },
};
</script>
<style scoped lang="scss">
::v-deep .modal.auto-report-modal {
  background: linear-gradient(180deg, #090909 0%, #636363 100%);
  color: #fff;
  max-height: calc(100vh - 30px);
  max-width: 920px;
  overflow: hidden;
  padding: 0;
  position: relative;
  width: calc(100% - 30px);
}

.auto-report-modal__header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 9px 14px 0;
}

.auto-report-modal__header h2 {
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.25;
  margin: 0;
}

.auto-report-modal__close {
  background: transparent;
  border: none;
  color: #fff;
  cursor: pointer;
  width: fit-content;
}

.auto-report-modal__content {
  display: flex;
  flex-direction: column;
  gap: 0;
  max-height: calc(100vh - 70px);
  overflow: auto;
  padding: 4px 14px 26px;
}

.auto-report-modal__content--audio-active {
  align-items: center;
  justify-content: center;
  min-height: min(570px, calc(100vh - 120px));
  min-height: min(570px, calc(100dvh - 120px));
  overflow: hidden;
}

.auto-report-modal__audio-activity {
  align-items: center;
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  min-height: 0;
  padding: 18px;
  width: 100%;
}

.auto-report-modal__audio-visual {
  background: transparent;
  border: 0;
  display: block;
  flex: 0 1 auto;
  height: clamp(200px, 40vw, 280px);
  padding: 0;
  width: clamp(200px, 40vw, 280px);

  img {
    display: block;
    height: 100%;
    object-fit: contain;
    width: 100%;
  }
}

.auto-report-modal__audio-stop {
  cursor: pointer;
}

.auto-report-modal__audio-stop:hover,
.auto-report-modal__audio-stop:focus-visible {
  filter: drop-shadow(0 0 12px rgba(225, 25, 25, 0.45));
  outline: none;
}

.auto-report-modal__audio-status {
  color: rgba(255, 255, 255, 0.9);
  font-size: 18px;
  font-weight: 700;
  line-height: 1.35;
  margin: 20px 0 0;
  text-align: center;
}

.auto-report-modal__audio-status--recording {
  color: #ffd2ce;
}

.auto-report-modal__intro,
.auto-report-modal__hint {
  color: rgba(255, 255, 255, 0.74);
  font-size: 13px;
  font-style: italic;
  line-height: 1.35;
  margin: 0;
}

.auto-report-modal__intro {
  max-width: 670px;
}

.auto-report-modal__input-row {
  align-items: center;
  display: flex;
  gap: 6px;
  margin: 38px auto 32px;
  max-width: 565px;
  width: 100%;
}

.auto-report-modal__field {
  flex: 1 1 auto;
  min-width: 0;
}

::v-deep .auto-report-modal__field .base-field__control {
  background: #fff;
  border: none;
  border-radius: 24px;
  height: 40px;
  margin: 0;
  overflow: hidden;
}

::v-deep .auto-report-modal__field .base-field__input {
  background: transparent;
  border: none;
  color: #242424;
  font-size: 14px;
  height: 40px;
  padding: 0 18px;
  width: 100%;
}

.auto-report-modal__round-button {
  align-items: center;
  background: #fff;
  border: 2px solid transparent;
  border-radius: 50%;
  color: #050505;
  cursor: pointer;
  display: flex;
  flex: 0 0 40px;
  height: 40px;
  justify-content: center;
  padding: 8px;
  transition: transform 0.2s, border-color 0.2s;
  width: 40px;
}

.auto-report-modal__round-button:hover {
  transform: translateY(-1px);
}

.auto-report-modal__round-button:disabled {
  cursor: wait;
  opacity: 0.55;
  transform: none;
}

.auto-report-modal__round-button--selected {
  border-color: #56be54;
}

.auto-report-modal__round-button--recording {
  animation: auto-report-pulse 1.2s ease-in-out infinite;
  border-color: #e04a3f;
}

.auto-report-modal__round-button img {
  display: block;
  height: 21px;
  object-fit: contain;
  width: 21px;
}

.auto-report-modal__file-input {
  display: none;
}

.auto-report-modal__submit {
  height: 1px;
  opacity: 0;
  overflow: hidden;
  padding: 0;
  pointer-events: none;
  position: absolute;
  width: 1px;
}

.auto-report-modal__selected-file {
  color: rgba(255, 255, 255, 0.78);
  font-size: 11px;
  margin: -23px auto 17px;
  max-width: 565px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
}

.auto-report-modal__response {
  margin: -14px auto 22px;
  max-width: 565px;
  width: 100%;
}

.auto-report-modal__status {
  color: rgba(255, 255, 255, 0.86);
  font-size: 12px;
  margin: 0;
}

.auto-report-modal__status--recording {
  color: #ffd2ce;
}

.auto-report-modal__status--error {
  color: #ffb8b1;
}

.auto-report-modal__review {
  margin: -10px auto 4px;
  max-width: 850px;
  width: 100%;
}

.auto-report-modal__review-hint,
.auto-report-modal__review-empty {
  color: rgba(255, 255, 255, 0.82);
  font-size: 12px;
  line-height: 1.4;
  margin: 0 0 8px;
}

.auto-report-modal__review-table-wrap {
  background: #fff;
  border-radius: 16px;
  color: #242424;
  overflow: visible;
  padding: 12px;
}

.auto-report-modal__review-table {
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;
}

.auto-report-modal__review-table th {
  color: #565656;
  font-size: 11px;
  font-weight: 700;
  padding: 0 4px 6px;
  text-align: left;
}

.auto-report-modal__review-table th:first-child {
  width: 42%;
}

.auto-report-modal__review-table th:nth-child(2),
.auto-report-modal__review-table th:nth-child(3),
.auto-report-modal__review-table th:nth-child(4) {
  width: 17%;
}

.auto-report-modal__review-table th:last-child {
  width: 7%;
}

.auto-report-modal__review-table td {
  padding: 4px;
  vertical-align: top;
}

::v-deep .auto-report-modal__review-field .base-field__control {
  background: #0786c1;
  border: none;
  border-radius: 7px;
  color: #fff;
  height: 38px;
  margin: 0;
}

::v-deep .auto-report-modal__review-field .base-field__input {
  background: transparent;
  border: none;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  height: 38px;
  padding: 0 10px;
  width: 100%;
}

::v-deep .auto-report-modal__review-field .base-field__input::placeholder {
  color: rgba(255, 255, 255, 0.7);
}

::v-deep .auto-report-modal__review-field .base-field__dropdown {
  min-width: 240px;
  z-index: 5;
}

.auto-report-modal__remove-row {
  align-items: center;
  background: #efefef;
  border: none;
  border-radius: 50%;
  color: #8b2929;
  cursor: pointer;
  display: flex;
  font-size: 20px;
  height: 32px;
  justify-content: center;
  margin: 3px auto 0;
  width: 32px;
}

.auto-report-modal__warnings {
  color: #ffd7a1;
  font-size: 12px;
  margin: 10px 0 0;
  padding-left: 18px;
}

.auto-report-modal__review-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 14px;
}

.auto-report-modal__review-actions .btn {
  margin: 0;
  min-width: 130px;
  width: auto;
}

.auto-report-modal__add-row {
  background: rgba(255, 255, 255, 0.18);
}

.auto-report-modal__apply {
  background: #56be54;
}

@keyframes auto-report-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(224, 74, 63, 0.45);
  }
  50% {
    box-shadow: 0 0 0 7px rgba(224, 74, 63, 0);
  }
}

.auto-report-modal__example {
  align-items: center;
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(0, 1fr) auto;
  margin-bottom: 28px;
}

.auto-report-modal__example-text {
  font-size: 13px;
  font-style: italic;
  line-height: 1.55;
}

.auto-report-modal__example-text strong {
  display: block;
  margin-bottom: 1px;
}

.auto-report-modal__example-text p {
  color: rgba(255, 255, 255, 0.84);
  margin: 0;
}

.auto-report-modal__examples {
  align-items: flex-start;
  display: flex;
  gap: 14px;
}

.auto-report-modal__example-action {
  align-items: center;
  background: transparent;
  border: none;
  color: #fff;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  font-size: 10px;
  height: auto;
  line-height: 1.15;
  padding: 0;
  text-align: center;
  width: 84px;
}

.auto-report-modal__example-action:hover .auto-report-modal__example-tile,
.auto-report-modal__example-action:focus-visible .auto-report-modal__example-tile {
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.7),
    0 4px 12px rgba(0, 0, 0, 0.2);
}

.auto-report-modal__example-action:focus-visible {
  outline: none;
}

.auto-report-modal__example-action--image {
  cursor: zoom-in;
}

.auto-report-modal__example-tile {
  align-items: center;
  background: #dedede;
  border-radius: 10px;
  color: #000;
  display: flex;
  height: 84px;
  justify-content: center;
  margin-bottom: 5px;
  overflow: hidden;
  transition: box-shadow 0.2s;
  width: 84px;

  img {
    height: 100%;
    object-fit: cover;
    width: 100%;
  }
}

.auto-report-modal__example-tile .auto-report-modal__play-icon {
  height: 51px;
  object-fit: contain;
  width: 51px;
}

.auto-report-modal__hint {
  max-width: 340px;
}

.auto-report-modal__image-preview {
  align-items: center;
  background: rgba(0, 0, 0, 0.86);
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 48px 24px 24px;
  position: absolute;
  z-index: 3;
}

.auto-report-modal__image-preview img {
  max-height: 100%;
  max-width: 100%;
  object-fit: contain;
}

.auto-report-modal__image-preview button {
  background: transparent;
  border: none;
  color: #fff;
  cursor: pointer;
  font-size: 32px;
  position: absolute;
  right: 14px;
  top: 8px;
}

@media screen and (max-width: 680px) {
  ::v-deep .modal.auto-report-modal {
    max-height: calc(100vh - 20px);
    max-height: calc(100dvh - 20px);
    width: calc(100% - 20px);
  }

  ::v-deep .modal.auto-report-modal > .auto-report-modal__content {
    max-height: calc(100vh - 70px);
    max-height: calc(100dvh - 70px);
    padding-bottom: 18px;
  }

  ::v-deep
    .modal.auto-report-modal
    > .auto-report-modal__content--audio-active {
    min-height: calc(100vh - 90px);
    min-height: calc(100dvh - 90px);
  }

  .auto-report-modal__audio-activity {
    padding: 10px 0 18px;
  }

  .auto-report-modal__audio-visual {
    height: min(76vw, 360px);
    width: min(76vw, 360px);
  }

  .auto-report-modal__audio-status {
    font-size: 15px;
    max-width: 340px;
  }

  .auto-report-modal__input-row {
    margin: 24px 0 28px;
  }

  .auto-report-modal__example {
    gap: 20px;
    grid-template-columns: 1fr;
    margin-bottom: 24px;
  }

  .auto-report-modal__examples {
    justify-content: center;
    width: 100%;
  }

  .auto-report-modal__review-table,
  .auto-report-modal__review-table tbody {
    display: block;
  }

  .auto-report-modal__review-table thead {
    display: none;
  }

  .auto-report-modal__review-table tbody {
    display: grid;
    gap: 12px;
  }

  .auto-report-modal__review-table tr {
    border-bottom: 1px solid #dedede;
    display: grid;
    gap: 6px;
    grid-template-columns: repeat(3, minmax(0, 1fr)) 32px;
    padding-bottom: 12px;
  }

  .auto-report-modal__review-table tr:last-child {
    border-bottom: 0;
    padding-bottom: 0;
  }

  .auto-report-modal__review-table td {
    display: flex;
    flex-direction: column;
    min-width: 0;
    padding: 0;
  }

  .auto-report-modal__review-table td[data-label]::before {
    color: #565656;
    content: attr(data-label);
    font-size: 10px;
    font-weight: 700;
    margin: 0 0 4px 2px;
  }

  .auto-report-modal__review-table td:first-child {
    grid-column: 1 / -1;
  }

  .auto-report-modal__review-table td:last-child {
    justify-content: flex-end;
  }

  .auto-report-modal__review-table td:last-child .auto-report-modal__remove-row {
    margin: 0;
  }

  ::v-deep .auto-report-modal__review-field .base-field__dropdown {
    min-width: 100%;
  }
}

@media screen and (max-width: 420px) {
  .auto-report-modal__header h2 {
    font-size: 13px;
  }

  .auto-report-modal__round-button {
    flex-basis: 38px;
    height: 38px;
    width: 38px;
  }

}
</style>
